import { action, makeObservable, observable, observe, runInAction } from 'mobx';

import CompanyStore from './CompanyStore';
import {
  addPaymentMethod,
  removePaymentMethod,
  updatePaymentMethod,
  createVivaAccount,
  getVivaAccountStatus,
} from '../api/admin';

import { PaymentMethod, VivaOnboardingStatus } from '../model/PaymentMethod';
import Company from '../model/Company';
import { CreateVivaAccountDTO, VivaAccountStatus } from '../model/Viva';
import { PaymentMethodConfigTypes } from '../model/PaymentMethodConfig';
import { Message, MessageSeverity } from '../model/Message';
import EditPaymentMethodStore from './EditPaymentMethodStore';
import invalidationManager from './FetcherStores/InvalidationManager';

export enum PaymentMethodState {
  IDLE,
  LOADING,
  ERROR,
}
class PaymentMethodStore {
  companyStore: CompanyStore;

  editPaymentMethodStore: EditPaymentMethodStore;

  paymentMethods: PaymentMethod[] = [];

  state: PaymentMethodState = PaymentMethodState.LOADING;

  vivaStatus: VivaAccountStatus | null = null;

  vivaOnboardingStatus = {
    onboardingStatus: '',
    onboardingUrl: '',
  };

  vivaLoading = false;

  message: Message = { text: '', severity: undefined };

  vivaMessage: Message = { text: '', severity: undefined };

  constructor(companyStore: CompanyStore, editPaymentMethodStore: EditPaymentMethodStore) {
    this.companyStore = companyStore;
    this.editPaymentMethodStore = editPaymentMethodStore;

    makeObservable(this, {
      companyStore: observable,
      editPaymentMethodStore: observable,
      paymentMethods: observable,
      state: observable,
      vivaStatus: observable,
      vivaLoading: observable,
      message: observable,
      vivaMessage: observable,
      addPayMethod: action,
      removePayMethod: action,
      updatePayMethod: action,
      setMessage: action,
      resetMessage: action,
      setVivaMessage: action,
      resetVivaMessage: action,
      handleCreateVivaAccount: action,
      handleGetVivaAccountStatus: action,
      setVivaAccountStatus: action,
      setVivaOnboardingStatus: action,
    });

    observe(companyStore, 'data', () => {
      if (!this.companyStore.data) return;

      const company = this.companyStore.data as Company;
      this.getPaymentMethods(company);
    });
  }

  getPaymentMethods(company: Company) {
    if (!company) {
      return;
    }

    if (company.paymentMethods) {
      this.paymentMethods = Object.entries(
        company.paymentMethods as { [key: string]: PaymentMethod },
      ).map(([key, paymentMethod]) => ({
        ...paymentMethod,
        id: key,
        active: paymentMethod.active,
      })) as PaymentMethod[];
    }
    this.state = PaymentMethodState.IDLE;
  }

  addPayMethod = async (paymentMethod: Partial<PaymentMethod>) => {
    if (!this.companyStore.companyKey) {
      return;
    }

    const existingTidypayMethod = this.paymentMethods.some(
      (method) => method.type === PaymentMethodConfigTypes.TIDYPAY,
    );

    const existingVivaWalletMethod = this.paymentMethods.some(
      (method) => method.type === PaymentMethodConfigTypes.VIVA_WALLET,
    );

    if (existingTidypayMethod && paymentMethod.type === PaymentMethodConfigTypes.TIDYPAY) {
      throw new Error('Betalmetoden Tidypay finns redan');
    }

    if (existingVivaWalletMethod && paymentMethod.type === PaymentMethodConfigTypes.VIVA_WALLET) {
      throw new Error('Betalmetoden Viva Wallet finns redan');
    }

    runInAction(() => {
      this.state = PaymentMethodState.LOADING;
    });

    addPaymentMethod(this.companyStore.companyKey, paymentMethod)
      .then((paymethod: PaymentMethod) => {
        invalidationManager.invalidate(['company', this.companyStore.companyKey, 'paymentMethods']);
        runInAction(() => {
          this.paymentMethods.push(paymethod);
          this.state = PaymentMethodState.IDLE;
        });
      })
      .catch((err) => {
        this.message = err.message;
        this.state = PaymentMethodState.ERROR;
      });
  };

  removePayMethod = async (id: string) => {
    if (!this.companyStore.companyKey || !id) {
      return;
    }

    await removePaymentMethod(this.companyStore.companyKey, id)
      .then(() => {
        runInAction(() => {
          this.paymentMethods = this.paymentMethods.filter(
            (paymentMethod) => paymentMethod.id !== id,
          );
        });
      })
      .catch((err) => {
        this.message = err.message;
      });
  };

  updatePayMethod = async (paymentMethod: Partial<PaymentMethod>) => {
    if (!this.companyStore.companyKey || !paymentMethod.id) {
      return;
    }

    await updatePaymentMethod(this.companyStore.companyKey, paymentMethod.id, paymentMethod)
      .then(() => {
        invalidationManager.invalidate(['company', this.companyStore.companyKey, 'paymentMethods']);
        runInAction(() => {
          const index = this.paymentMethods.findIndex((method) => method.id === paymentMethod.id);

          if (index !== -1) {
            this.paymentMethods[index] = {
              ...this.paymentMethods[index],
              ...(paymentMethod as PaymentMethod),
            };
          }
        });
      })
      .catch((err) => {
        this.setMessage(err.message, 'error');
      });
  };

  handleCreateVivaAccount = async (vivaAccountDTO: CreateVivaAccountDTO) => {
    if (!this.companyStore.companyKey) {
      return;
    }

    const existingPaymentMethod = this.paymentMethods.some(
      (method) => method.type === PaymentMethodConfigTypes.VIVA_WALLET,
    );

    if (existingPaymentMethod) {
      throw new Error('Viva wallet finns redan som betalmetod');
    }

    await createVivaAccount(this.companyStore.companyKey, vivaAccountDTO)
      .then(({ paymentMethod }) => {
        runInAction(() => {
          this.paymentMethods.push(paymentMethod);
          this.state = PaymentMethodState.IDLE;
          this.setVivaOnboardingStatus(paymentMethod.onboardingStatus, paymentMethod.onboardingUrl);
        });
      })
      .catch((error) => {
        this.setMessage(error.message, 'error');
      });
  };

  handleGetVivaAccountStatus = async () => {
    if (!this.companyStore.companyKey) {
      return;
    }

    this.vivaLoading = true;

    await getVivaAccountStatus(this.companyStore.companyKey)
      .then((res) => {
        this.setVivaAccountStatus(res);
        const onboardingStatus = res.verified
          ? VivaOnboardingStatus.VERIFIED
          : VivaOnboardingStatus.IN_PROGRESS;
        if (
          this.editPaymentMethodStore.paymentMethod?.type === PaymentMethodConfigTypes.VIVA_WALLET
        ) {
          this.editPaymentMethodStore.paymentMethod.onboardingStatus = onboardingStatus;
        }
        this.vivaLoading = false;
      })
      .catch((error) => {
        this.setMessage(error.message, 'error');
        this.vivaLoading = false;
      });
  };

  setMessage = (text: string, severity: MessageSeverity) => {
    this.message = { text, severity };
  };

  resetMessage = () => {
    this.message = { text: '', severity: undefined };
  };

  setVivaMessage = (text: string, severity: MessageSeverity) => {
    this.vivaMessage = { text, severity };
  };

  resetVivaMessage = () => {
    this.vivaMessage = { text: '', severity: undefined };
  };

  setVivaAccountStatus = (status: VivaAccountStatus) => {
    this.vivaStatus = status;
  };

  setVivaOnboardingStatus = (onboardingStatus: string, onboardingUrl: string) => {
    this.vivaOnboardingStatus = { onboardingStatus, onboardingUrl };
  };

  resetVivaOnboardingStatus = () => {
    this.vivaOnboardingStatus = { onboardingStatus: '', onboardingUrl: '' };
  };
}

export default PaymentMethodStore;
